// src/pages/GardenCrops.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowBack, ArrowForward, Edit } from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import PlantingForm from "../components/PlantingForm";
import PlotCard from "../components/PlotCard";
import GardenTodoList from "../components/GardenTodoList";
import GardenLogBook from "../components/GardenLogBook";
import EventForm from "../components/EventForm";
import GardenCropsListView from "../components/GardenCropsListView";
import GardenCropsPlanView from "../components/GardenCropsPlanView";
import RotationPlannerView from "../components/RotationPlannerView";
import FruitAssignmentForm from "../components/FruitAssignmentForm";
import CompostManagementForm from "../components/CompostManagementForm";
import { formatDate } from "../utils/dateUtils";

const GardenCrops = () => {
  const { garden_uuid, view: viewParam, year: yearParam, plot_group_uuid: plotGroupParam } = useParams();
  const navigate = useNavigate();

  // Initialize from URL params or defaults
  const initialYear = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();
  const initialView = viewParam || "list";

  const [plots, setPlots] = useState([]);
  const [plantedCrops, setPlantedCrops] = useState([]);
  const [crops, setCrops] = useState([]);
  const [garden, setGarden] = useState({});
  const [plotGroups, setPlotGroups] = useState([]);
  const [year, setYear] = useState(initialYear);
  const [viewMode, setViewMode] = useState(initialView);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedPlanted, setSelectedPlanted] = useState(null);
  const [isEventModalOpen, setEventModalOpen] = useState(false);
  const [eventFormState, setEventFormState] = useState(null);
  const [eventsVersion, setEventsVersion] = useState(0);
  
  // New modal states for fruit and compost forms
  const [isFruitModalOpen, setIsFruitModalOpen] = useState(false);
  const [fruitFormState, setFruitFormState] = useState(null);
  const [isCompostModalOpen, setIsCompostModalOpen] = useState(false);
  const [compostFormState, setCompostFormState] = useState(null);
  const [selectedPlotGroupForForm, setSelectedPlotGroupForForm] = useState(null);
  const [cropTypeFilter, setCropTypeFilter] = useState(null);

  const fetchPlotsAndCrops = async () => {
    try {
      const [plotsRes, gardenRes, cropsRes, plantedRes, plotGroupsRes] = await Promise.all([
        apiClient.get(`/gardens/${garden_uuid}/plots`),
        apiClient.get(`/gardens/${garden_uuid}`),
        apiClient.get(`/crops`),
        apiClient.get(`/gardens/${garden_uuid}/crops`),
        apiClient.get(`/plot_groups`),
      ]);
      setPlots(plotsRes.data);
      setGarden(gardenRes.data);
      setCrops(cropsRes.data);
      setPlantedCrops(plantedRes.data);
      setPlotGroups(plotGroupsRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  };

  useEffect(() => {
    fetchPlotsAndCrops();
  }, [garden_uuid]);

  // Update state when URL params change
  useEffect(() => {
    if (yearParam) {
      const parsedYear = parseInt(yearParam, 10);
      if (!isNaN(parsedYear) && parsedYear !== year) {
        setYear(parsedYear);
      }
    } else if (!yearParam) {
      // If no year param, navigate to include year
      const currentYear = new Date().getFullYear();
      const plotGroupPart = plotGroupParam ? `/${plotGroupParam}` : '';
      navigate(`/gardens/${garden_uuid}/${viewMode}/${currentYear}${plotGroupPart}`);
    }
  }, [yearParam, garden_uuid, viewMode, plotGroupParam, navigate]);

  useEffect(() => {
    if (viewParam && viewParam !== viewMode) {
      setViewMode(viewParam);
    } else if (!viewParam) {
      // If no view param, navigate to include view
      const plotGroupPart = plotGroupParam ? `/${plotGroupParam}` : '';
      navigate(`/gardens/${garden_uuid}/list/${year}${plotGroupPart}`);
    }
  }, [viewParam, garden_uuid, year, plotGroupParam, navigate]);

  const handleModalOpen = (plantedOrPlot = null, selectedYear = null, plotGroupUuid = null, cropTypeFilterParam = null) => {
    // If it's a plot object (from card button), create a minimal planted object with just plot_uuid
    if (plantedOrPlot && plantedOrPlot.plot_uuid && !plantedOrPlot.planted_uuid) {
      const targetYear = selectedYear || year;
      
      // Find crops already planted in this plot for this year
      const plotCropsForYear = plantedCrops.filter(
        (p) =>
          p.plot_uuid === plantedOrPlot.plot_uuid &&
          p.date_planted &&
          new Date(p.date_planted).getFullYear() === targetYear
      );
      
      // Default to March 1st (spring planting season)
      let defaultDate = `${targetYear}-03-01`;
      
      // If there are crops, use the latest one's date + 1 day
      if (plotCropsForYear.length > 0) {
        // Sort by date_planted descending to get the latest
        const sortedCrops = [...plotCropsForYear].sort(
          (a, b) => new Date(b.date_planted) - new Date(a.date_planted)
        );
        const latestCrop = sortedCrops[0];
        const latestDate = new Date(latestCrop.date_planted);
        latestDate.setDate(latestDate.getDate() + 1);
        
        // Format as YYYY-MM-DD
        defaultDate = latestDate.toISOString().split('T')[0];
      }
      
      setSelectedPlanted({
        plot_uuid: plantedOrPlot.plot_uuid,
        crop_uuid: "",
        date_planted: defaultDate,
        rows: "",
        method: "",
        orientation: "",
        placement_mode: "",
      });
      
      // Store plot_group_uuid for the form
      setSelectedPlotGroupForForm(plotGroupUuid);
    } else {
      setSelectedPlanted(plantedOrPlot ? { ...plantedOrPlot } : null);
      setSelectedPlotGroupForForm(null);
    }
    // Store crop type filter if provided
    setCropTypeFilter(cropTypeFilterParam);
    setModalOpen(true);
  };
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedPlanted(null);
    setSelectedPlotGroupForForm(null);
    setCropTypeFilter(null);
  };

  const handleEventModalOpen = (initialData = null) => {
    setEventFormState(initialData);
    setEventModalOpen(true);
  };
  const handleEventModalClose = () => setEventModalOpen(false);

  const handleEditEvent = (event) => {
    handleEventModalOpen(event);
  };

  // New handlers for fruit and compost modals
  const handleFruitModalOpen = (plantedOrPlot = null) => {
    // If it's a plot object (from card button), create form state with plot_uuid
    if (plantedOrPlot && plantedOrPlot.plot_uuid) {
      setFruitFormState({
        plot_uuid: plantedOrPlot.plot_uuid,
        crop_uuid: "",
        date_planted: "",
        rows: "",
        method: "",
        orientation: "",
        placement_mode: "",
      });
    } else {
      // If it's a planted object or null, use as is
      setFruitFormState(plantedOrPlot);
    }
    setIsFruitModalOpen(true);
  };
  const handleFruitModalClose = () => {
    setIsFruitModalOpen(false);
    setFruitFormState(null);
  };

  const handleCompostModalOpen = (planted = null) => {
    setCompostFormState(planted);
    setIsCompostModalOpen(true);
  };
  const handleCompostModalClose = () => {
    setIsCompostModalOpen(false);
    setCompostFormState(null);
  };

  const filteredPlantedCrops = plantedCrops.filter(
    (planted) => {
      if (!planted.date_planted) return false;
      
      const plantedYear = new Date(planted.date_planted).getFullYear();
      const cropTypeName = planted.crop?.crop_type?.name?.toLowerCase();
      
      // Perennial crops (fruits and herbs) should be visible for all years after planting
      const isPerennial = cropTypeName === 'fruit' || cropTypeName === 'herb';
      
      if (isPerennial) {
        // Show perennial crops if they were planted in or before the selected year
        return plantedYear <= year;
      } else {
        // For annual crops (vegetables, etc.), only show crops planted in the selected year
        return plantedYear === year;
      }
    }
  );

  const cropsByPlot = (() => {
    // Filter out Visual Element plots and group by type
    const filteredPlots = plots.filter(plot => 
      plot.plot_type?.name?.toLowerCase() !== 'visual element' &&
      plot.type !== "Building"
    );
    
    // Group plots by type in the specified order
    const groupedPlots = {
      'vegetable-bed': filteredPlots.filter(plot => 
        plot.plot_type?.name?.toLowerCase() === 'vegetable bed'
      ),
      'fruit-tree': filteredPlots.filter(plot => 
        plot.plot_type?.name?.toLowerCase() === 'fruit tree'
      ),
      'compost-bin': filteredPlots.filter(plot => 
        plot.plot_type?.name?.toLowerCase() === 'compost bin'
      )
    };
    
    // Flatten the grouped plots in the specified order
    const orderedPlots = [
      ...groupedPlots['vegetable-bed'],
      ...groupedPlots['fruit-tree'],
      ...groupedPlots['compost-bin']
    ];
    
    return orderedPlots.map((plot) => ({
      plot,
      crops: filteredPlantedCrops.filter(
        (p) => p.plot_uuid === plot.plot_uuid
      ),
    }));
  })();

  return (
    <Box>
      <Typography variant="h3" gutterBottom className="vintage-script-header">
        {garden.name}
      </Typography>

      {/* View Mode Controls - Centered under header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }} className="garden-view-controls">
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, v) => {
            if (v === 'manage-plots') {
              // Navigate to plots page
              navigate(`/gardens/${garden_uuid}/plots`);
              return;
            }
            if (v) {
              setViewMode(v);
              // Only include plot_group_uuid in URL for rotation view
              const plotGroupPart = (v === 'rotation' && plotGroupParam) ? `/${plotGroupParam}` : '';
              navigate(`/gardens/${garden_uuid}/${v}/${year}${plotGroupPart}`);
            }
          }}
          className="garden-view-toggle-group"
        >
          <ToggleButton value="list">List View</ToggleButton>
          <ToggleButton value="rotation">Rotation View</ToggleButton>
          <ToggleButton value="tasks">Log book</ToggleButton>
          <ToggleButton value="manage-plots" className="manage-plots-toggle-button">
            <Edit />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Year Navigation - Centered under controls */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mb: 3 }}>
        <Button 
          variant="outlined" 
          onClick={() => {
            const newYear = year - 1;
            setYear(newYear);
            const plotGroupPart = plotGroupParam ? `/${plotGroupParam}` : '';
            navigate(`/gardens/${garden_uuid}/${viewMode}/${newYear}${plotGroupPart}`);
          }}
          className="vintage-button"
          sx={{ minWidth: 'auto', padding: '8px 12px' }}
        >
          ☜
        </Button>
        <Typography variant="h5" sx={{ fontFamily: 'var(--font-decorative)', color: 'var(--color-sepia)', fontWeight: 600 }}>
          {year}
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => {
            const newYear = year + 1;
            setYear(newYear);
            const plotGroupPart = plotGroupParam ? `/${plotGroupParam}` : '';
            navigate(`/gardens/${garden_uuid}/${viewMode}/${newYear}${plotGroupPart}`);
          }}
          className="vintage-button"
          sx={{ minWidth: 'auto', padding: '8px 12px' }}
        >
          ☞
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        {viewMode === "tasks" && (
          <Button
            variant="outlined"
            onClick={() => handleEventModalOpen()}
            className="vintage-button"
          >
            ✚ Add Entry
          </Button>
        )}
      </Box>

      {viewMode === "tasks" ? (
        <GardenLogBook
          key={`logbook-${year}`}
          garden_uuid={garden_uuid}
          onEditEvent={handleEditEvent}
          refreshKey={eventsVersion}
          year={year}
        />
      ) : viewMode === "list" ? (
        <GardenCropsListView
          filteredPlantedCrops={filteredPlantedCrops}
          plots={plots}
          handleModalOpen={handleModalOpen}
          onPlanCrop={handleModalOpen}
          garden_uuid={garden_uuid}
          year={year}
          navigate={navigate}
          onAssignFruit={handleFruitModalOpen}
        />
      ) : viewMode === "rotation" ? (
        <RotationPlannerView
          plots={plots}
          plantedCrops={plantedCrops}
          garden_uuid={garden_uuid}
          year={year}
          plotGroups={plotGroups}
          initialPlotGroup={plotGroupParam || ''}
          onNavigate={navigate}
          onPlanCrop={handleModalOpen}
          onAssignFruit={handleFruitModalOpen}
          onManageCompost={handleCompostModalOpen}
          onEditPlanted={handleModalOpen}
          onPlantedUpdated={fetchPlotsAndCrops}
        />
      ) : (
        <GardenCropsPlanView
          cropsByPlot={cropsByPlot}
          garden_uuid={garden_uuid}
          year={year}
          onNavigate={navigate}
          onPlanCrop={handleModalOpen}
          onAssignFruit={handleFruitModalOpen}
          onManageCompost={handleCompostModalOpen}
          onEditPlanted={handleModalOpen}
        />
      )}

      <PlantingForm
        plots={plots}
        crops={crops}
        planted={selectedPlanted}
        open={isModalOpen}
        onClose={handleModalClose}
        onSave={fetchPlotsAndCrops}
        garden_uuid={garden_uuid}
        year={year}
        plot_group_uuid={selectedPlotGroupForForm}
        cropTypeFilter={cropTypeFilter}
      />

      <FruitAssignmentForm
        plots={plots}
        crops={crops}
        planted={fruitFormState}
        plantedCrops={plantedCrops}
        open={isFruitModalOpen}
        onClose={handleFruitModalClose}
        onSave={fetchPlotsAndCrops}
        garden_uuid={garden_uuid}
        year={year}
      />

      <CompostManagementForm
        plots={plots}
        crops={crops}
        planted={compostFormState}
        open={isCompostModalOpen}
        onClose={handleCompostModalClose}
        onSave={fetchPlotsAndCrops}
        garden_uuid={garden_uuid}
        year={year}
      />

      <Dialog
        open={isEventModalOpen}
        onClose={handleEventModalClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {eventFormState?.event_uuid ? "Edit Task" : "Add New Task"}
        </DialogTitle>
        <DialogContent>
          <EventForm
            garden_uuid={garden_uuid}
            plots={plots}
            plantedCrops={plantedCrops}
            initialData={eventFormState}
            onSubmit={async (payload) => {
              try {
                if (eventFormState?.event_uuid) {
                  await apiClient.put(
                    `/gardens/${garden_uuid}/events/${eventFormState.event_uuid}`,
                    payload
                  );
                } else {
                  await apiClient.post(`/gardens/${garden_uuid}/events`, payload);
                }
                handleEventModalClose();
                fetchPlotsAndCrops();
                setEventsVersion((v) => v + 1);
              } catch (err) {
                console.error("Failed to save event:", err);
              }
            }}
            onCancel={handleEventModalClose}
            onDelete={eventFormState?.event_uuid ? async () => {
              try {
                await apiClient.delete(
                  `/gardens/${garden_uuid}/events/${eventFormState.event_uuid}`
                );
                handleEventModalClose();
                fetchPlotsAndCrops();
                setEventsVersion((v) => v + 1);
              } catch (err) {
                console.error("Failed to delete event:", err);
              }
            } : null}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GardenCrops;
